import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { useBlockGroupContext } from "components/form/Blocks";
import { AttachmentType } from "components/ui/AttachmentPicker";

import { useSaveActionsContext } from "context/save";

import useSectionOneApi from "hooks/api/private/blocks/useSectionOneApi";
import useMobileOrDesktopFile from "hooks/useMobileOrDesktopFile";
import { ButtonDeleteInterface } from "hooks/useTitleFunctionsTopBlock";

import { SectionOneBody, SectionOneDetails } from "types/api/blocks/sectionOne";
import { DesktopMobileForm, LanguageEnum } from "types/api/commons";
import { DesktopOrMobile } from "types/enums";

import { converterAttachmentToFileWithPayload, createAttachmentType } from "utils/utils";
import { getExtraErrorsDescription, notEmptyMedia, possibleEmptyMedia } from "utils/validators";

export type SectionOneForm = {
    id: string | undefined;
    main_img_id: string | undefined;
    main_img: DesktopMobileForm;
    background_img_id: string | undefined;
    background_img: DesktopMobileForm;
    text: {
        [x in LanguageEnum]: {
            title: string;
            subtitle: string;
            description: string;
        };
    };
};

type SectionOneType = {
    setBackgroundImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    setMainImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    buttonDelete: ButtonDeleteInterface;
    formHook: FormHookType<SectionOneForm>;
};

function useSectionOne(): SectionOneType {
    const { blockGroupCompositionId, blockId, graphicalOrder, blockGroupId } =
        useBlockGroupContext();

    const { saveSectionOne, getOneSectionOne, deleteSectionOne } = useSectionOneApi();

    const [schema, setSchema] = React.useState<ObjectPropertyType>({
        type: "object",
        properties: {
            id: {
                type: "simple",
                validators: [],
            },
            main_img_id: {
                type: "simple",
                validators: [],
            },
            background_img_id: {
                type: "simple",
                validators: [],
            },
            main_img: possibleEmptyMedia,
            background_img: notEmptyMedia,
            text: {
                type: "collection",
                of: {
                    type: "object",
                    properties: {
                        title: {
                            type: "simple",
                            validators: [],
                        },
                        subtitle: {
                            type: "simple",
                            validators: [],
                        },
                        description: {
                            type: "simple",
                            validators: [],
                        },
                    },
                },
            },
        },
    });

    const initialValues: SectionOneForm = React.useMemo(() => {
        return {
            id: undefined,
            main_img_id: undefined,
            main_img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            background_img_id: undefined,
            background_img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            text: {
                it: {
                    title: "",
                    subtitle: "",
                    description: "",
                },
                en: {
                    title: "",
                    subtitle: "",
                    description: "",
                },
            },
        };
    }, []);

    const formHook = useForm<SectionOneForm>(schema, initialValues, getExtraErrorsDescription);

    const { showAllError, setForm, isValid, form } = formHook;

    React.useEffect(() => {
        setSchema((prevSchema) => {
            const newSchema = _.cloneDeep(prevSchema);

            if (form.main_img.desktop?.content || form.main_img.mobile?.content) {
                newSchema.properties.main_img = notEmptyMedia;
            } else {
                newSchema.properties.main_img = possibleEmptyMedia;
            }

            return newSchema;
        });
    }, [form.main_img.desktop?.content, form.main_img.mobile?.content]);

    const { setDesktopMobileImage: setBackgroundImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "background_img",
    });

    const { setDesktopMobileImage: setMainImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "main_img",
    });

    const [sectionOneDetails, setSectionOneDetails] = React.useState<SectionOneDetails | undefined>(
        undefined
    );

    React.useEffect(() => {
        const [apiCall, abortController] = getOneSectionOne(
            { id: blockGroupCompositionId },
            { rejectNotFound: true }
        );

        apiCall.then(setSectionOneDetails).catch((error) => console.log(error));

        return () => abortController.abort();
    }, [setSectionOneDetails, getOneSectionOne, blockGroupCompositionId]);

    React.useEffect(() => {
        if (sectionOneDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = sectionOneDetails.id;
                newForm.background_img_id = sectionOneDetails.background_img_id;
                newForm.main_img_id = sectionOneDetails.main_img_id;
                newForm.text = sectionOneDetails.text;

                newForm.background_img = {
                    id: sectionOneDetails.background_img.id,
                    desktop: createAttachmentType({
                        id: sectionOneDetails.background_img.desktop.id,
                        label: "Desktop",
                        filename: sectionOneDetails.background_img.desktop.filename,
                        status: 2,
                    }),
                    mobile: createAttachmentType({
                        id: sectionOneDetails.background_img.mobile.id,
                        label: "Mobile",
                        filename: sectionOneDetails.background_img.mobile.filename,
                        status: 2,
                    }),
                };
                if (sectionOneDetails.main_img) {
                    newForm.main_img = {
                        id: sectionOneDetails.main_img.id,
                        desktop: createAttachmentType({
                            id: sectionOneDetails.main_img.desktop.id,
                            label: "Desktop",
                            filename: sectionOneDetails.main_img.desktop.filename,
                            status: 2,
                        }),
                        mobile: createAttachmentType({
                            id: sectionOneDetails.main_img.mobile.id,
                            label: "Mobile",
                            filename: sectionOneDetails.main_img.mobile.filename,
                            status: 2,
                        }),
                    };
                }

                return newForm;
            });
        }
    }, [setForm, sectionOneDetails]);

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction(blockGroupCompositionId, () => {
            if (isValid) {
                const promises: Promise<unknown>[] = [];

                const sectionOneBody: SectionOneBody = {
                    id: blockGroupCompositionId,
                    main_img: {
                        id: form.main_img.id,
                        desktop: converterAttachmentToFileWithPayload(form.main_img.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.main_img.mobile),
                    },
                    background_img: {
                        id: form.background_img.id,
                        desktop: converterAttachmentToFileWithPayload(form.background_img.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.background_img.mobile),
                    },
                    text: {
                        it: {
                            title: form.text.it.title,
                            subtitle: form.text.it.subtitle,
                            description: form.text.it.description,
                        },
                        en: {
                            title: form.text.en.title,
                            subtitle: form.text.en.subtitle,
                            description: form.text.en.description,
                        },
                    },
                };

                const [apiCall] = saveSectionOne(sectionOneBody);

                promises.push(apiCall);

                return Promise.all(promises);
            } else {
                showAllError();
                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        saveSectionOne,
        addSaveAction,
        showAllError,
        blockGroupCompositionId,
        blockGroupId,
        blockId,
        graphicalOrder,
        isValid,
        form,
    ]);

    return React.useMemo(
        (): SectionOneType => ({
            buttonDelete: deleteSectionOne,
            setBackgroundImage,
            setMainImage,
            formHook,
        }),
        [deleteSectionOne, setBackgroundImage, setMainImage, formHook]
    );
}

export default useSectionOne;
